// [PURPOSE]: Sub-plan 1C item 1 — pure helpers for charged-item default
//            semantics (current = max when missing; preserve consumed state).
//
// [WHY]: CustomItemCreator inlined this rule on 2026-05-19 (after a user
//        screenshot showed "Cast Forbiddance 1 ch." rendering with an empty
//        current count). EditItemDialog still strips `charges: 0` to `''`
//        on load AND fails to default `charges` to `maxCharges` on save —
//        same underlying gap, different surface. This helper centralizes the
//        rule so EditItemDialog, the InventoryItem constructor (App.jsx:331),
//        and any future consumer (Spec 3 spellCastingService refactor)
//        consume one truth.
//
// [SCOPE — v1 minimum]: Just the default + clamp + format. Spec 3's larger
//        scope (per-spell `costPerCast`, item-level pool refactor of
//        spellCastingService, v5→v6 migration, BASE_ITEMS data edits) is
//        DEFERRED to a follow-up commit so the EditItemDialog surface can
//        ship without dragging the whole spec along.
//
// [SPEC]: docs/superpowers/specs/2026-05-18-charged-item-shared-pool-semantics-design.md (D1, partial)

/**
 * Returns the canonical `charges` value for a pooled item, given the user's
 * intent for `current` and the pool's `maxCharges`.
 *
 * Decision table:
 *   - maxCharges <= 0 or missing → returns `null` (no pool — not a charged item)
 *   - maxCharges > 0:
 *       - current is a finite number ≥ 0 → returns min(current, maxCharges)
 *       - current is NaN / null / undefined / '' / negative → returns maxCharges (default full)
 *
 * Accepts both numbers and numeric strings (EditItemDialog stores inputs as
 * strings until save).
 *
 * @param {number|string|null|undefined} current
 * @param {number|string|null|undefined} maxCharges
 * @returns {number|null}
 */
export function resolveCurrentCharges(current, maxCharges) {
  const max = typeof maxCharges === 'number' ? maxCharges : parseInt(maxCharges, 10);
  if (!Number.isFinite(max) || max <= 0) return null;

  const cur = typeof current === 'number' ? current : parseInt(current, 10);
  if (!Number.isFinite(cur) || cur < 0) return max;

  return Math.min(cur, max);
}

/**
 * Formats a charges/maxCharges value for display in a form input. The
 * EditItemDialog load path used `value || ''` previously, which incorrectly
 * stripped `0` (consumed state) to an empty string. This helper preserves 0.
 *
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function formatChargeFieldValue(value) {
  if (value == null) return '';
  return String(value);
}
